































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import axios from '@/zenky/api/Http';
import { XIcon } from 'vue-feather-icons';
import SelectField from '@/components/forms/fields/common/SelectField.vue';
import Multiselect from 'vue-multiselect';
import DateTime from '@/zenky/interfaces/common/DateTime';
import { OrderV3 as Order } from '@/zenky/interfaces/orders/Order';
import OrderStatus from '@/zenky/interfaces/orders/OrderStatus';
import OrderStatusKind from '@/zenky/interfaces/orders/OrderStatusKind';
import OrderStatusChange from '@/zenky/interfaces/orders/OrderStatusChange';
import Enum from '@/zenky/interfaces/common/Enum';
import { Omit } from 'lodash'; // Can remove when we upgrade TypeScript.
import City from '@/zenky/interfaces/store/City';
import FormFieldOption from '@/zenky/forms/interfaces/FormFieldOption';
import store from '@/store/index';
import SkeletonText from '@/components/typography/SkeletonText.vue';

type StubOrderStatus = (
  Omit<Order['current_status']['status'], 'kind' | 'created_at' | 'updated_at' | 'sorting' | 'visible'>
  & { kind: Enum<'stub'> }
  & {
    stubStatus: true,
    id: 'stub';
    name: 'Черновик',
    background_color: '#CDB9B5', // eslint-disable-line camelcase
    kind: { id: 'stub', name: 'Черновик' },
  }
)
function isStubStatus(status: Exclude<OrderHeader['status'], null>): status is StubOrderStatus {
  const key: keyof StubOrderStatus = 'stubStatus';
  return key in status;
}
function resourceLoading(resource: OrderHeader['cities'] | OrderHeader['orderStatuses']): resource is null {
  return resource === null;
}

export type StatusSelectEvent = Exclude<OrderHeader['orderStatusesOptions'], null>[number];
export type CitySelectEvent = City['id'];

@Component({ components: { SelectField, Multiselect, XIcon, SkeletonText } })
export default class OrderHeader extends Vue {
  @Prop({ required: true }) readonly city!: Pick<City, 'id' | 'name'> | null;
  @Prop({ required: true }) readonly submittedAt!: Order['submitted_at'];
  @Prop({ required: true }) readonly number!: Order['number'] | null;
  @Prop({ required: true }) readonly status!: Order['current_status']['status'] | StubOrderStatus | null;
  @Prop({ required: true, type: Boolean }) readonly orderLoading!: boolean;
  citiesLoading: boolean = false;
  orderStatusesLoading: boolean = false;

  get cities(): City[] | null {
    return store.state.helpers.storeCities;
  }
  get cityOptions(): FormFieldOption[] {
    if (resourceLoading(this.cities)) {
      if (this.city === null) {
        return [];
      }
      return [{ value: this.city.id, label: this.city.name }];
    }
    return this.cities.map(city => ({ value: city.id, label: city.name }));
  }
  get showCitySelect() {
    return this.submittedAt === null;
  }
  async loadCities() {
    this.citiesLoading = true;
    await store.dispatch('helpers/loadStoreCities');
    this.citiesLoading = false;
  }

  get orderStatuses(): OrderStatus[] | null {
    return store.state.helpers.orderStatuses;
  }
  get orderStatusesOptions(): OrderStatus[] {
    if (resourceLoading(this.orderStatuses)) {
      return [];
    }
    return this.orderStatuses;
  }
  async loadOrderStatuses() {
    this.orderStatusesLoading = true;
    await store.dispatch('helpers/loadOrderStatuses');
    this.orderStatusesLoading = false;
  }
  get canChangeStatus(): boolean {
    if (
      this.status === null
      || isStubStatus(this.status)
      || [OrderStatusKind.CANCELLED, OrderStatusKind.REJECTED, OrderStatusKind.PENDING].includes(this.status.kind.id)
    ) {
      return false;
    }
    return true;
  }

  getDatetimeText(): string {
    if (this.submittedAt === null) {
      throw new Error('Submit date is `null`');
    }
    const date = this.submittedAt.datetime; // '2019-08-01 12:40:16'
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября',
      'ноября', 'декабря'];
    return `${date.slice(-8, -3)}, ${parseInt(date.slice(8, 10), 10)} ${months[parseInt(date.slice(5, 7), 10) - 1]}`;
  }

  @Watch('canChangeStatus', { immediate: true })
  onCanChangeStatusChange(newVal: this['canChangeStatus']) {
    if (newVal === true) {
      if (this.orderStatuses === null) {
        this.loadOrderStatuses();
      }
    }
  }
  @Watch('showCitySelect', { immediate: true })
  onShowCitySelectChange(newVal: this['showCitySelect']) {
    if (newVal === true) {
      if (this.cities === null) {
        this.loadCities();
      }
    }
  }
}
